import type { UserRole } from "@/lib/user-roles"
import {
  getSharedAuthTestIdentity,
  type SharedIdentity,
} from "../platform-auth/client"
import { getCharacterPlatformApiUrlFromEnv } from "../platformConfig.js"

const DEFAULT_CHARACTER_PLATFORM_BACKEND_URL = "http://localhost:8000"
const SHARED_IDENTITY_TIMEOUT_MS = 8000

export class SharedIdentityRequestError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = "SharedIdentityRequestError"
    this.status = status
  }
}

export type CharacterPlatformBackendMe = SharedIdentity & {
  roles?: UserRole[]
}

export type SharedIdentityResponse = CharacterPlatformBackendMe

export function getCharacterPlatformBackendUrl() {
  return (
    getCharacterPlatformApiUrlFromEnv() ??
    DEFAULT_CHARACTER_PLATFORM_BACKEND_URL
  )
}

export async function fetchSharedIdentity(
  accessToken: string
): Promise<SharedIdentityResponse> {
  const testIdentity = getSharedAuthTestIdentity(accessToken)
  if (testIdentity) {
    return testIdentity
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), SHARED_IDENTITY_TIMEOUT_MS)

  let response: Response
  try {
    response = await fetch(
      new URL("/v1/identity/me", getCharacterPlatformBackendUrl()),
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        cache: "no-store",
        signal: controller.signal,
      }
    )
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new SharedIdentityRequestError(
        `Shared identity request timed out after ${SHARED_IDENTITY_TIMEOUT_MS}ms`,
        504
      )
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
  }

  if (!response.ok) {
    const errorBody = await response.text()
    throw new SharedIdentityRequestError(
      `Shared identity request failed with ${response.status}: ${errorBody || "empty response"}`,
      response.status
    )
  }

  return response.json()
}

export async function syncSharedIdentity(accessToken: string) {
  return fetchSharedIdentity(accessToken)
}

export function isSharedIdentityMissingError(error: unknown) {
  return (
    error instanceof SharedIdentityRequestError && error.status === 404
  )
}

export const fetchBackendMe = fetchSharedIdentity
export const syncBackendUser = syncSharedIdentity
